import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActiveModal, TabSetComponent } from '@healthcatalyst/cashmere';
import { TenantSchedule } from 'src/app/shared/models/tenant-schedule';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import BaseComponent from 'src/app/shared/components/base.component';
import { TenantScheduleService } from 'src/app/core/services/application/tenant-schedule.service';
import { createCronValidator } from 'src/app/shared/validators/cron-validator';
import { CronService } from 'src/app/shared/components/cron-editor/cron.service';

@Component({
    selector: 'cdp-tenant-schedule-modal',
    templateUrl: './tenant-schedule-modal.component.html',
    styleUrls: ['./tenant-schedule-modal.component.scss'],
    standalone: false
})
export class TenantScheduleModalComponent extends BaseComponent implements OnInit {
    @ViewChild('tabSet') tabSet!: TabSetComponent;

    selectedTabIndex!: number;
    scheduleInfoInvalidIndicatorDisplayed!: boolean;
    connectionDetailsMode!: string;
    currentCronStr = '';

    tenantId!: string;
    isEdit!: boolean;
    tenantScheduleForm!: FormGroup;
    activeIngestionJobCount = 0;
    showScheduleDeactiveWarning = false;

    saveInProgress!: boolean;
    saveErrorMessage!: string;
    public tenantSchedule!: TenantSchedule;

    descriptionHelp =
        'A label to identify this schedule. Consider naming based on source system, subject area, or cadence.';
    activeFlagHelp = 'Jobs on the schedule will only be run if the schedule is active.';

    constructor(
        private activeModal: ActiveModal,
        private fb: FormBuilder,
        private tenantScheduleService: TenantScheduleService,
        private toasterService: ToastNotificationService,
        private cronService: CronService,
        oktaAuth: OktaAuthService
    ) {
        super(oktaAuth);
    }

    get shouldDisplayScheduleInfoInvalidIndicator(): boolean {
        const value =
            (this.selectedTabIndex == 1 || this.scheduleInfoInvalidIndicatorDisplayed) && !this.isScheduleInfoValid();
        if (value) {
            this.scheduleInfoInvalidIndicatorDisplayed = true;
        }

        return value;
    }

    ngOnInit(): void {
        this.tenantId = this.activeModal.data?.tenantId;
        this.isEdit = !!this.activeModal.data?.schedule;
        this.tenantSchedule = this.activeModal.data?.schedule || <TenantSchedule>{ activeFlag: true };
        this.activeIngestionJobCount = this.activeModal.data?.schedule?.activeIngestionJobCount || 0;
        this.initForm();
        this.saveInProgress = false;
    }

    onCronChanged(data: { rawCron: string; formGroup: FormGroup; controlName: string }) {
        // update the sched name if the user hasn't explicitly set it to something other than the cron string
        const newCronStr = this.cronService.translateCronWithUtc(data.rawCron);
        const currentSchedName = this.tenantScheduleForm.controls['description'].value;
        if (!currentSchedName || this.currentCronStr === currentSchedName) {
            this.tenantScheduleForm.controls['description'].setValue(newCronStr);
        }
        this.currentCronStr = newCronStr;

        // update the form with the new cron string
        data.formGroup.controls[data.controlName].setValue(data.rawCron);
        data.formGroup.controls[data.controlName].markAsDirty();
    }

    setNameToMatchCron(): void {
        this.tenantScheduleForm.controls['description'].setValue(this.currentCronStr);
    }

    initForm(): void {
        this.tenantScheduleForm = this.fb.group({
            description: new FormControl(this.tenantSchedule.description, [Validators.required]),
            cron: new FormControl(this.tenantSchedule.cron, {
                validators: [Validators.required],
                asyncValidators: [
                    createCronValidator(expression => this.tenantScheduleService.validateCron(expression))
                ],
                updateOn: 'blur'
            }),
            activeFlag: new FormControl(this.tenantSchedule.activeFlag)
        });
        this.currentCronStr = this.cronService.translateCronWithUtc(this.tenantSchedule.cron);
        if (this.isEdit && this.activeIngestionJobCount > 0) {
            this.tenantScheduleForm.controls['activeFlag'].valueChanges
                .pipe(this.unsubscribeOnDestroy())
                .subscribe(active => {
                    if (!active) {
                        this.showScheduleDeactiveWarning = true;
                    } else {
                        this.showScheduleDeactiveWarning = false;
                    }
                });
        }
    }

    isScheduleInfoValid(): boolean {
        return this.tenantScheduleForm.controls['description'].valid && this.tenantScheduleForm.controls['cron'].valid;
    }

    save(): void {
        this.saveInProgress = true;
        this.saveErrorMessage = '';
        const tenantScheduleRequest = <TenantSchedule>{};
        tenantScheduleRequest.tenantId = this.tenantId;
        tenantScheduleRequest.description = this.tenantScheduleForm.controls['description'].value;
        tenantScheduleRequest.cron = this.tenantScheduleForm.controls['cron'].value;
        tenantScheduleRequest.activeFlag = this.tenantScheduleForm.controls['activeFlag'].value;
        tenantScheduleRequest.updatedUser = this.oktaUser.username;
        if (this.isEdit) {
            tenantScheduleRequest.tenantScheduleId = this.tenantSchedule.tenantScheduleId;
        }

        const save = this.isEdit
            ? this.tenantScheduleService.updateTenantSchedule(tenantScheduleRequest)
            : this.tenantScheduleService.saveTenantSchedule(tenantScheduleRequest);
        save.subscribe({
            next: data => {
                this.saveInProgress = false;
                this.toasterService.showSuccess(
                    `Successfully ${this.isEdit ? 'updated' : 'created new'} schedule '${tenantScheduleRequest.description}'.`,
                    `Saved Schedule`
                );
                this.activeModal.close({ ...tenantScheduleRequest, tenantScheduleId: data.id });
            },
            error: error => {
                this.saveInProgress = false;

                console.error(error);
                let message = `Unable to save schedule '${tenantScheduleRequest.description}'. `;
                message += error.status != 500 ? error.error : `Contact an admin if the problem continues.`;
                this.toasterService.showError(message, 'Save Failed');
            }
        });
    }

    cancel(): void {
        this.activeModal.dismiss();
    }
}
